import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Theme,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ListIncomingRoutes } from '../components/ListIncomingRoutes';
import {
  DocumentFile,
  RouteType,
  TerminalRouteStatus,
  useGetProductionGroupLazyQuery,
  useCheckLegsHaveAnyDangerGoodsLazyQuery,
  RecievedStatus,
  useUpdateTrafficGridsConfigMutation,
  useGetTrafficGridsConfigQuery,
  useGetMeQuery,
  useLegsListUpdateSubSubscription,
  useTemplateRouteUpdateSubSubscription,
  useTerminalStatusTruckFillAndTimeUpdateSubscription,
} from '../generated/graphql';
import { isDateTimeFormat } from '../lib/date_time';
import { useHttpClient } from '../providers/HttpClientProvider';
import { useUserConfiguration } from '../providers/UserConfigurationProvider';
import { Maybe } from 'graphql/jsutils/Maybe';
import { RefreshOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import PreviousIcon from '@mui/icons-material/NavigateBefore';
import NextIcon from '@mui/icons-material/NavigateNext';
import { timeToMinute } from '../lib/time';
import ReportIcon from '@mui/icons-material/Report';
import ReportOffIcon from '@mui/icons-material/ReportOff';
import ToggleFullScreen from '../components/utils/toggleFullscreen';
import { AddUnplannedRouteModal } from '../components/modal/AddUnplannedRouteModal';

import { makeStyles } from '@mui/styles';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { GridFiltersDropdown } from '../utils/gridFiltersDropdown';
import { TABLE_NAMES } from '../lib/constants';
import { TerminalStatusModalUpdate } from '../components/datagrid/TerminalStatusModal';
import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridPinnedColumns,
  GridSortModel,
} from '@mui/x-data-grid-pro';

const useStyles = makeStyles((theme: Theme) => ({
  selectDateRoot: {
    display: 'flex',
    padding: theme.spacing(1.5, 2.5),
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 14,
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.06)',
    '& form > *': {
      margin: theme.spacing(0, 1),
    },
  },
  filterButtonRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  checkBoxBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: 8,
    paddingRight: 4,
    paddingLeft: 10,
    backgroundColor: theme.palette.grey[50],
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#f0faf0',
    },
  },
  checkBox: {
    padding: 3,
  },
  kpiBox: {
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      margin: theme.spacing(0, 0.5),
    },
  },
  rootGrid: {
    marginTop: 2,
    paddingTop: 0,
    position: 'relative',
  },
  btnSelected: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light,
  },
  inputValTime: {
    height: 15,
    width: 130,
    fontSize: 14,
  },
  fullScreenBtnLeft: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    background: '#fff',
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
      borderColor: theme.palette.primary.light,
    },
  },
  secondaryToolbar: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    borderRadius: 12,
    backgroundColor: '#fafbfc',
    border: '1px solid',
    borderColor: theme.palette.divider,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  navButton: {
    borderRadius: 8,
    height: 36,
    width: 36,
    border: '1px solid',
    borderColor: theme.palette.divider,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: '#fff',
      boxShadow: '0 2px 6px rgba(105,188,70,0.35)',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.grey[300],
    },
  },
  actionButton: {
    borderRadius: 8,
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.8125rem',
    padding: theme.spacing(0.5, 1.5),
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  exportButton: {
    borderRadius: 8,
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.8125rem',
    padding: theme.spacing(0.5, 1.5),
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
    },
  },
  trafficTypeSelect: {
    borderRadius: 8,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
      transition: 'border-color 0.2s ease',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

dayjs.extend(customParseFormat);

const OUTPUT_FORMAT = 'YYYY-MM-DD HH:mm';

type TftDateRangeValue = [Dayjs | null, Dayjs | null];

type ShortcutChangeImportance = 'accept' | 'set';

type AlwaysEnabledShortcutsProps = {
  items?: {
    id?: string;
    label: string;
    getValue: (params: {
      isValid: (value: TftDateRangeValue) => boolean;
    }) => TftDateRangeValue;
  }[];
  changeImportance?: ShortcutChangeImportance;
  isLandscape?: boolean;
  isValid: (value: TftDateRangeValue) => boolean;
  onChange: (
    newValue: TftDateRangeValue,
    changeImportance: ShortcutChangeImportance,
    shortcut: { id?: string; label: string },
  ) => void;
  className?: string;
  sx?: any;
};

const AlwaysEnabledShortcuts = ({
  items,
  changeImportance = 'accept',
  isValid,
  onChange,
  className,
  sx,
}: AlwaysEnabledShortcutsProps) => {
  if (!items?.length) return null;

  return (
    <List
      className={className}
      dense
      sx={[
        { maxWidth: 200, overflow: 'auto' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {items.map(({ getValue, ...item }) => {
        const newValue = getValue({ isValid });

        return (
          <ListItem key={item.id ?? item.label}>
            <Chip
              label={item.label}
              onClick={() => onChange(newValue, changeImportance, item)}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

const parseDateTimeValue = (value: string | undefined | null): Dayjs | null => {
  if (!value) return null;

  const strictParsed = dayjs(value, OUTPUT_FORMAT, true);
  if (strictParsed.isValid()) {
    return strictParsed;
  }

  const fallbackParsed = dayjs(value);
  return fallbackParsed.isValid() ? fallbackParsed : null;
};

export interface LegsResponse {
  id: number;
  arrivalTime: string;
  departureTime?: Maybe<string>;
  actualArrivalTime?: Maybe<string>;
  actualArrivalDate?: Maybe<string>;
  actualDepartureTime?: Maybe<string>;
  actualDepartureDate?: Maybe<string>;
  actualDockTime?: Maybe<string>;
  actualDockDate?: Maybe<string>;
  actualUndockTime?: Maybe<string>;
  actualUndockDate?: Maybe<string>;
  deletedAt: Maybe<string>;
  productionDate: string;
  transportationDate: string;
  actualCapacity?: Maybe<number>;
  loadCar?: Maybe<number>;
  loadCarPPL?: Maybe<number>;
  loadCages?: Maybe<number>;
  loadTrailer?: Maybe<number>;
  loadTrailerPPL?: Maybe<number>;
  unloadCar?: Maybe<number>;
  unloadCarPPL?: Maybe<number>;
  unloadCages?: Maybe<number>;
  unloadTrailer?: Maybe<number>;
  unloadTrailerPPL?: Maybe<number>;
  arrivingPallets?: Maybe<number>;
  arrivingPalletSpace?: Maybe<number>;
  isLoadingInCar?: boolean;
  isLoadingInTrailer?: boolean;
  carDetails?: {
    id?: number | undefined;
    actualCapacity?: Maybe<number> | undefined;
    registrationNumber?: Maybe<string> | undefined;
    gateNumber?: Maybe<string> | undefined;
    togref?: Maybe<string> | undefined;
    containerId?: Maybe<string> | undefined;
  };
  trailerDetails?: {
    actualCapacity?: Maybe<number> | undefined;
    registrationNumber?: Maybe<string> | undefined;
    gateNumber?: Maybe<string> | undefined;
    togref?: Maybe<string> | undefined;
    containerId?: Maybe<string> | undefined;
  };
  timeObj?: {
    originalArrTime: string;
    actualArrTime: string;
    originalDepartTime: string;
    actualDepartTime: string;
    terminalName: string;
    legId: number;
    transportationDate: string;
    totalPallets: number;
    totalPalletSpace: number;
  }[];
  note: string;
  unload: boolean;
  load: boolean;
  gateNumber?: Maybe<string> | undefined;
  carRegistrationNumber?: Maybe<string> | undefined;
  trailerRegistrationNumber?: Maybe<string> | undefined;
  terminalNickname?: Maybe<string> | undefined;
  terminalNote: string;
  lastUpdatedBy?: Maybe<string>;
  updatedAt: string;
  isNonArrivalMarked?: boolean;
  nonArrivalMarkedFrom?: Maybe<string>;
  isSealBroken?: Maybe<boolean>;
  sealNumber?: Maybe<string> | undefined;
  productionGroup?: Maybe<string> | undefined;
  terminalResponsible?: Maybe<string> | undefined;
  operator?: Maybe<string> | undefined;
  priority?: Maybe<string> | undefined;
  terminalStatus?: Maybe<string> | undefined;
  terminalStatusDescription?: Maybe<string> | undefined;
  terminalStatusUpdatedAt?: Maybe<string> | undefined;
  terminalStatusLog?:
    | Array<{
        id: string;
        status: string;
        timestamp: string;
        legId: number;
        locationId: number;
        locationOrder: number;
        locationLabel: string;
        locationName: string;
        createdBy: string;
        updatedBy?: string | null;
        updatedAt?: string | null;
        gate?: string | null;
      }>
    | undefined;
  arrivalStatus?: Maybe<string> | undefined;
  receivedStatus?: Maybe<RecievedStatus> | undefined;
  receivedBy?: Maybe<string> | undefined;
  timeWhenReceived?: Maybe<string> | undefined;
  kippReceivedBy?: Maybe<string> | undefined;
  kippTimeWhenReceived?: Maybe<string> | undefined;
  assigned?: Maybe<boolean> | undefined;
  ansvBefraktare?: Maybe<string> | undefined;
  timeWhenAssigned?: Maybe<string> | undefined;
  turnummer?: Maybe<number> | number | undefined;
  bpx?: Maybe<number> | undefined;
  pri03?: Maybe<number> | undefined;
  pri49?: Maybe<number> | undefined;
  upri03?: Maybe<number> | undefined;
  upri49?: Maybe<number> | undefined;
  parti?: Maybe<number> | undefined;
  brev?: Maybe<number> | undefined;
  pru?: Maybe<number> | undefined;
  pip?: Maybe<number> | undefined;
  tom?: Maybe<boolean> | undefined;
  driverName?: Maybe<string>;
  togref?: Maybe<string> | undefined;
  rutekode?: Maybe<string> | undefined;
  trainNumber?: Maybe<string> | undefined;
  inneholdsbeskrivelse?: Maybe<string> | undefined;
  containerId?: Maybe<string> | undefined;
  transportType?: Maybe<string> | undefined;
  files: Pick<
    DocumentFile,
    'id' | 'size' | 'path' | 'mimetype' | 'originalname'
  >[];
  loadingListItems: {
    pallets?: Maybe<number>;
    palletSpace?: Maybe<number>;
    isDangerous: boolean;
  }[];
  location?: Maybe<{
    id: number;
    city: string;
    country: string;
    locationShortCode: string;
    type: string;
  }>;
  route: {
    rutekode: any;
    turnummer: any;
    trainNumber: any;
    adr: any;
    inneholdsbeskrivelse: any;
    id: number;
    isUnplannedRoute: boolean;
    isCreatedFromTerminal: boolean;
    isCreatedFromPlanned: boolean;
    type: RouteType;
    routeId: string;
    capacity?: Maybe<number>;
    routeCreatedBy: string;
    routeLastUpdatedBy: string;
    routeUpdatedAt: Date;
    routeDeletedAt: Date;
    routeDeletedBy: string;
    trailerCarrierId: string;
    carCarrierId: string;
    tourRoute?: Maybe<{
      tourId: number;
    }>;
    transportationDate: string;
    driverName: string;
    subcontractorLocation: {
      id: number;
      name: string;
    };
    legs: {
      unload: boolean;
      load: boolean;
      deletedAt: Maybe<string>;
      location?: Maybe<{
        id: number;
        name: string;
        city: string;
        country: string;
        locationShortCode: string;
      }>;
    }[];
  };
}

type TerminalStatusLogItem = NonNullable<
  NonNullable<TerminalStatusModalUpdate['terminalStatusLog']>[number]
>;

function normalizeTerminalStatusLogForLeg(
  terminalStatusLog?: Array<TerminalStatusLogItem | null> | null,
) {
  return (terminalStatusLog ?? []).filter(
    (log): log is TerminalStatusLogItem => log != null,
  );
}

type TerminalStatusLegPatch = Omit<
  TerminalStatusModalUpdate,
  'legId' | 'routeId' | 'terminalStatusLog'
> & {
  terminalStatusLog?: Array<TerminalStatusLogItem | null> | null;
};

type NorwayColumnsUpdate = {
  id: string | number;
  legId?: number;
  containerId?: string;
  togref?: string;
  rutekode?: string;
  turnummer?: number | null;
  trainNumber?: string;
  adr?: string | null;
  inneholdsbeskrivelse?: string;
  bpx?: number | null;
  pri03?: number | null;
  pri49?: number | null;
  upri03?: number | null;
  upri49?: number | null;
  parti?: number | null;
  brev?: number | null;
  pru?: number | null;
  pip?: number | null;
  tom?: boolean;
};

function applyTerminalStatusLegPatch(
  currentLeg: LegsResponse,
  updatedStatus: TerminalStatusLegPatch,
): LegsResponse {
  return {
    ...currentLeg,
    ...(updatedStatus.actualArrivalDate !== undefined
      ? { actualArrivalDate: updatedStatus.actualArrivalDate }
      : {}),
    ...(updatedStatus.actualArrivalTime !== undefined
      ? { actualArrivalTime: updatedStatus.actualArrivalTime }
      : {}),
    ...(updatedStatus.actualDepartureDate !== undefined
      ? { actualDepartureDate: updatedStatus.actualDepartureDate }
      : {}),
    ...(updatedStatus.actualDepartureTime !== undefined
      ? { actualDepartureTime: updatedStatus.actualDepartureTime }
      : {}),
    ...(updatedStatus.actualDockDate !== undefined
      ? { actualDockDate: updatedStatus.actualDockDate }
      : {}),
    ...(updatedStatus.actualDockTime !== undefined
      ? { actualDockTime: updatedStatus.actualDockTime }
      : {}),
    ...(updatedStatus.actualUndockDate !== undefined
      ? { actualUndockDate: updatedStatus.actualUndockDate }
      : {}),
    ...(updatedStatus.actualUndockTime !== undefined
      ? { actualUndockTime: updatedStatus.actualUndockTime }
      : {}),
    ...(updatedStatus.terminalStatus !== undefined
      ? { terminalStatus: updatedStatus.terminalStatus ?? undefined }
      : {}),
    ...(updatedStatus.terminalStatusDescription !== undefined
      ? {
          terminalStatusDescription:
            updatedStatus.terminalStatusDescription ?? undefined,
        }
      : {}),
    ...(updatedStatus.terminalStatusUpdatedAt !== undefined
      ? {
          terminalStatusUpdatedAt:
            updatedStatus.terminalStatusUpdatedAt ?? undefined,
        }
      : {}),
    ...(updatedStatus.terminalStatusLog !== undefined
      ? {
          terminalStatusLog: normalizeTerminalStatusLogForLeg(
            updatedStatus.terminalStatusLog,
          ),
        }
      : {}),
    ...(updatedStatus.updatedAt !== undefined
      ? { updatedAt: updatedStatus.updatedAt ?? currentLeg.updatedAt }
      : {}),
    ...(updatedStatus.lastUpdatedBy !== undefined
      ? { lastUpdatedBy: updatedStatus.lastUpdatedBy ?? undefined }
      : {}),
    ...(updatedStatus.carGateNumber !== undefined
      ? {
          gateNumber: updatedStatus.carGateNumber ?? undefined,
          carDetails: {
            ...currentLeg.carDetails,
            gateNumber: updatedStatus.carGateNumber ?? undefined,
          },
        }
      : {}),
  };
}

function applySubscriptionRouteLegStatusAndTiming(
  currentLeg: LegsResponse,
  routeLeg: {
    actualArrivalDate?: Maybe<string>;
    actualArrivalTime?: Maybe<string>;
    actualDepartureDate?: Maybe<string>;
    actualDepartureTime?: Maybe<string>;
    actualDockDate?: Maybe<string>;
    actualDockTime?: Maybe<string>;
    actualUndockDate?: Maybe<string>;
    actualUndockTime?: Maybe<string>;
    terminalStatus?: Maybe<TerminalRouteStatus>;
    terminalStatusDescription?: Maybe<string>;
    terminalStatusUpdatedAt?: Maybe<string>;
    terminalStatusLog?: Array<TerminalStatusLogItem | null> | null;
    updatedAt?: Maybe<string>;
    lastUpdatedBy?: Maybe<string>;
  },
): LegsResponse {
  return applyTerminalStatusLegPatch(currentLeg, {
    actualArrivalDate: routeLeg.actualArrivalDate,
    actualArrivalTime: routeLeg.actualArrivalTime,
    actualDepartureDate: routeLeg.actualDepartureDate,
    actualDepartureTime: routeLeg.actualDepartureTime,
    actualDockDate: routeLeg.actualDockDate,
    actualDockTime: routeLeg.actualDockTime,
    actualUndockDate: routeLeg.actualUndockDate,
    actualUndockTime: routeLeg.actualUndockTime,
    terminalStatus: routeLeg.terminalStatus ?? null,
    terminalStatusDescription: routeLeg.terminalStatusDescription ?? null,
    terminalStatusUpdatedAt: routeLeg.terminalStatusUpdatedAt ?? null,
    terminalStatusLog: routeLeg.terminalStatusLog,
    updatedAt: routeLeg.updatedAt ?? null,
    lastUpdatedBy: routeLeg.lastUpdatedBy ?? null,
  });
}

function updateLegById(
  legs: LegsResponse[],
  legId: number,
  updater: (currentLeg: LegsResponse) => LegsResponse,
) {
  const index = legs.findIndex((leg) => leg.id === legId);
  if (index === -1) {
    return legs;
  }

  const currentLeg = legs[index];
  const nextLeg = updater(currentLeg);
  if (nextLeg === currentLeg) {
    return legs;
  }

  const next = [...legs];
  next[index] = nextLeg;
  return next;
}

interface TruckFillAndTimeProps {
  location: {
    id: number;
    name: string;
    shortCountryCode: string;
  };
}

export interface FilterConfiguration {
  inboundDomestic?: boolean;
  outboundDomestic?: boolean;
  inboundImport?: boolean;
  outboundExport?: boolean;
}

export interface TftReadOnlyConfigType {
  columnOrder?: string[];
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
  pinnedColumns?: GridPinnedColumns;
  gridColumnVisibilityModel?: GridColumnVisibilityModel;
  columnsWidth?: any;
  density?: 'compact' | 'standard' | 'comfortable';
  expandRowHeight?: boolean;
}

type TftCustomConfigType = {
  tftTime?: string;
  master_date?: string;
  isSameDay?: boolean;
  [key: string]: unknown;
};

type TftPersistedTableConfigType = TftReadOnlyConfigType & {
  custom?: TftCustomConfigType;
  userSelectedFilterId?: number;
  colVisibility?: GridColumnVisibilityModel;
  [key: string]: unknown;
};

type PendingTftDateSave = {
  startStr: string;
  endStr: string;
  isSameDay: boolean;
};

enum TFT_UPDATE_TYPES {
  UPDATE_EXTRA_COLUMNS = 'UPDATE_EXTRA_COLUMNS',
  UPDATE_LEG_DETAILS = 'UPDATE_LEG_DETAILS',
  UPDATE_VOLUME_DETAILS = 'UPDATE_VOLUME_DETAILS',
  UPDATE_CAR_TRAILER_DETAILS = 'UPDATE_CAR_TRAILER_DETAILS',
  TRUCK_FILL_AND_TIME = 'TRUCK_FILL_AND_TIME',
  PUBLISH_ROUTE_STATUS_UPDATE = 'PUBLISH_ROUTE_STATUS_UPDATE',
}

function applyNorwaySubscriptionPatchToSiblings(
  legs: LegsResponse[],
  subUpdatedLeg: any,
) {
  const updatedLeg = legs.find((leg) => leg.id === subUpdatedLeg.id);
  const routeId = subUpdatedLeg?.route?.id ?? updatedLeg?.route?.id;
  if (!routeId) {
    return legs;
  }

  const firstLoadingListItem = subUpdatedLeg?.carDetails?.loadingListItems?.[0];
  const togrefFromSubscription = subUpdatedLeg?.carDetails?.togref;
  const containerIdFromSubscription = subUpdatedLeg?.carDetails?.containerId;
  const tomFromSubscription = subUpdatedLeg?.tom;

  return legs.map((leg) => {
    if (leg.id === subUpdatedLeg.id || leg.route?.id !== routeId) {
      return leg;
    }

    let nextLeg = leg;
    if (
      subUpdatedLeg.route?.adr !== undefined ||
      subUpdatedLeg.route?.inneholdsbeskrivelse !== undefined ||
      subUpdatedLeg.route?.rutekode !== undefined ||
      subUpdatedLeg.route?.trainNumber !== undefined ||
      subUpdatedLeg.route?.turnummer !== undefined
    ) {
      nextLeg = {
        ...nextLeg,
        route: {
          ...nextLeg.route,
          ...(subUpdatedLeg.route?.adr !== undefined
            ? { adr: subUpdatedLeg.route.adr }
            : {}),
          ...(subUpdatedLeg.route?.inneholdsbeskrivelse !== undefined
            ? {
                inneholdsbeskrivelse: subUpdatedLeg.route.inneholdsbeskrivelse,
              }
            : {}),
          ...(subUpdatedLeg.route?.rutekode !== undefined
            ? { rutekode: subUpdatedLeg.route.rutekode }
            : {}),
          ...(subUpdatedLeg.route?.trainNumber !== undefined
            ? { trainNumber: subUpdatedLeg.route.trainNumber }
            : {}),
          ...(subUpdatedLeg.route?.turnummer !== undefined
            ? { turnummer: subUpdatedLeg.route.turnummer }
            : {}),
        },
      };
    }

    if (togrefFromSubscription !== undefined) {
      const nextCarDetails = {
        ...(nextLeg.carDetails ?? {}),
        togref: togrefFromSubscription,
      } as LegsResponse['carDetails'];
      nextLeg = {
        ...nextLeg,
        togref: togrefFromSubscription,
        carDetails: nextCarDetails,
      };
    }
    if (containerIdFromSubscription !== undefined) {
      const nextCarDetails = {
        ...(nextLeg.carDetails ?? {}),
        containerId: containerIdFromSubscription,
      } as LegsResponse['carDetails'];
      nextLeg = {
        ...nextLeg,
        containerId: containerIdFromSubscription,
        carDetails: nextCarDetails,
      };
    }
    if (tomFromSubscription !== undefined) {
      nextLeg = { ...nextLeg, tom: tomFromSubscription };
    }

    if (firstLoadingListItem?.volume) {
      nextLeg = {
        ...nextLeg,
        bpx: firstLoadingListItem.volume?.bpx,
        pri03: firstLoadingListItem.volume?.pri03,
        pri49: firstLoadingListItem.volume?.pri49,
        upri03: firstLoadingListItem.volume?.upri03,
        upri49: firstLoadingListItem.volume?.upri49,
        parti: firstLoadingListItem.volume?.parti,
        brev: firstLoadingListItem.volume?.brev,
        pru: firstLoadingListItem.volume?.pru,
        pip: firstLoadingListItem.volume?.pip,
      };
    }

    return nextLeg;
  });
}

function applyNorwayFieldsFromSubscription(
  currentLeg: LegsResponse,
  subUpdatedLeg: any,
) {
  const firstLoadingListItem = subUpdatedLeg?.carDetails?.loadingListItems?.[0];
  const nextLeg: LegsResponse = {
    ...currentLeg,
  };

  if (subUpdatedLeg.route?.adr !== undefined) {
    nextLeg.route = { ...nextLeg.route, adr: subUpdatedLeg.route.adr };
  }
  if (subUpdatedLeg.route?.inneholdsbeskrivelse !== undefined) {
    nextLeg.route = {
      ...nextLeg.route,
      inneholdsbeskrivelse: subUpdatedLeg.route.inneholdsbeskrivelse,
    };
  }
  if (subUpdatedLeg.route?.rutekode !== undefined) {
    nextLeg.route = {
      ...nextLeg.route,
      rutekode: subUpdatedLeg.route.rutekode,
    };
  }
  if (subUpdatedLeg.route?.trainNumber !== undefined) {
    nextLeg.route = {
      ...nextLeg.route,
      trainNumber: subUpdatedLeg.route.trainNumber,
    };
  }
  if (subUpdatedLeg.route?.turnummer !== undefined) {
    nextLeg.route = {
      ...nextLeg.route,
      turnummer: subUpdatedLeg.route.turnummer,
    };
  }

  if (subUpdatedLeg?.carDetails?.togref !== undefined) {
    nextLeg.togref = subUpdatedLeg.carDetails.togref;
    nextLeg.carDetails = {
      ...(nextLeg.carDetails ?? {}),
      togref: subUpdatedLeg.carDetails.togref,
    };
  }
  if (subUpdatedLeg?.carDetails?.containerId !== undefined) {
    nextLeg.containerId = subUpdatedLeg.carDetails.containerId;
    nextLeg.carDetails = {
      ...(nextLeg.carDetails ?? {}),
      containerId: subUpdatedLeg.carDetails.containerId,
    };
  }
  if (subUpdatedLeg?.tom !== undefined) {
    nextLeg.tom = subUpdatedLeg.tom;
  }

  if (firstLoadingListItem?.volume) {
    nextLeg.bpx = firstLoadingListItem.volume?.bpx;
    nextLeg.pri03 = firstLoadingListItem.volume?.pri03;
    nextLeg.pri49 = firstLoadingListItem.volume?.pri49;
    nextLeg.upri03 = firstLoadingListItem.volume?.upri03;
    nextLeg.upri49 = firstLoadingListItem.volume?.upri49;
    nextLeg.parti = firstLoadingListItem.volume?.parti;
    nextLeg.brev = firstLoadingListItem.volume?.brev;
    nextLeg.pru = firstLoadingListItem.volume?.pru;
    nextLeg.pip = firstLoadingListItem.volume?.pip;
  }

  if (subUpdatedLeg.updatedAt !== undefined) {
    nextLeg.updatedAt = subUpdatedLeg.updatedAt;
  }
  if (subUpdatedLeg.lastUpdatedBy !== undefined) {
    nextLeg.lastUpdatedBy = subUpdatedLeg.lastUpdatedBy;
  }
  if (subUpdatedLeg.operator !== undefined && subUpdatedLeg.operator !== null) {
    nextLeg.operator = subUpdatedLeg.operator;
  }

  return nextLeg;
}

export function TruckFillAndTime(props: TruckFillAndTimeProps) {
  const { t } = useTranslation();
  const {
    data: userConfigData,
    loading: loadingSavedTime,
    refetch: refetchTrafficGridsConfig,
  } = useGetTrafficGridsConfigQuery();
  const [updateTrafficGridsConfig] = useUpdateTrafficGridsConfigMutation();
  const { httpClient } = useHttpClient();
  const { globalDateFormat } = useUserConfiguration();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { location } = props;
  const MAX_RANGE_DAYS = 5;
  const [gridTop, setGridTop] = useState(0);
  const [productionGroup, setProductionGroup] = useState('');
  const [isOneDanger, setOneDanger] = useState<number>(0);
  const [errorInfo, setErrorInfo] = useState<{
    errorInField: boolean;
    errorMessage: string;
  }>();
  const [isProdGroupFetched, setProductionGroupFetched] =
    useState<boolean>(false);
  const [productionGroupArray, setProductionGroupArray] = useState<string[]>(
    [],
  );
  const [tftConfig, setTftConfig] = useState<TftReadOnlyConfigType>({
    columnOrder: [],
    filterModel: { items: [] },
    pinnedColumns: { left: ['routeId', 'loadType'], right: [] },
    columnsWidth: {},
    density: 'compact',
    sortModel: [
      {
        field: 'transportationDate',
        sort: 'asc',
      },
      {
        field: 'arrivalTime',
        sort: 'asc',
      },
    ],
    gridColumnVisibilityModel: {
      id: false,
      newArrivalTime: false,
      arrivalCountry: false,
      departureCountry: false,
      carCarrierId: false,
      carCarrierIdQR: false,
      trailerCarrierId: false,
      trailerCarrierIdQR: false,
    },
    expandRowHeight: false,
  });
  const classes = useStyles();
  const dayjsDisplayFormat = useMemo(() => {
    const dateFormat = globalDateFormat || 'yyyy-MM-dd HH:mm';
    return dateFormat.replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD');
  }, [globalDateFormat]);

  const TRAFFIC_TYPE_OPTIONS = ['Inbound', 'Outbound', 'Import', 'Export'];
  const [selectedTrafficType, setSelectedTrafficType] = useState<string>('');
  const [legsData, setLegsData] = useState<LegsResponse[]>([]);
  const [allRouteLegsIdArr, setallRouteLegsIdArr] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [unplannedRouteModalOpen, setUnplannedRouteModalOpen] = useState(false);
  const [tftStartDate, setTftStartDate] = useState<string>('');
  const [tftEndDate, setTftEndDate] = useState<string>('');
  const [loadIncomingApi, setLoadIncomingApi] = useState<boolean>(false);

  const handleTerminalStatusUpdated = useCallback(
    (updatedStatus: TerminalStatusModalUpdate) => {
      setLegsData((prev) =>
        updateLegById(prev, updatedStatus.legId, (currentLeg) =>
          applyTerminalStatusLegPatch(currentLeg, updatedStatus),
        ),
      );
    },
    [],
  );

  const handleNorwayColumnsUpdated = useCallback(
    (updatedRow: NorwayColumnsUpdate) => {
      if (!updatedRow.legId) {
        return;
      }

      setLegsData((prev) =>
        updateLegById(prev, updatedRow.legId as number, (currentLeg) => {
          const nextLeg = { ...currentLeg };

          if (updatedRow.containerId !== undefined) {
            nextLeg.containerId = updatedRow.containerId;
            nextLeg.carDetails = {
              ...(nextLeg.carDetails ?? {}),
              containerId: updatedRow.containerId,
            } as LegsResponse['carDetails'];
          }
          if (updatedRow.togref !== undefined) {
            nextLeg.togref = updatedRow.togref;
            nextLeg.carDetails = {
              ...(nextLeg.carDetails ?? {}),
              togref: updatedRow.togref,
            } as LegsResponse['carDetails'];
          }

          if (updatedRow.rutekode !== undefined) {
            nextLeg.rutekode = updatedRow.rutekode;
          }
          if (updatedRow.turnummer !== undefined) {
            nextLeg.turnummer = updatedRow.turnummer;
          }
          if (updatedRow.trainNumber !== undefined) {
            nextLeg.trainNumber = updatedRow.trainNumber;
          }
          if (updatedRow.adr !== undefined) {
            nextLeg.route = {
              ...nextLeg.route,
              adr: updatedRow.adr,
            };
          }
          if (updatedRow.inneholdsbeskrivelse !== undefined) {
            nextLeg.inneholdsbeskrivelse = updatedRow.inneholdsbeskrivelse;
          }

          if (updatedRow.rutekode !== undefined) {
            nextLeg.route = {
              ...nextLeg.route,
              rutekode: updatedRow.rutekode,
            };
          }
          if (updatedRow.turnummer !== undefined) {
            nextLeg.route = {
              ...nextLeg.route,
              turnummer: updatedRow.turnummer,
            };
          }
          if (updatedRow.trainNumber !== undefined) {
            nextLeg.route = {
              ...nextLeg.route,
              trainNumber: updatedRow.trainNumber,
            };
          }
          if (updatedRow.inneholdsbeskrivelse !== undefined) {
            nextLeg.route = {
              ...nextLeg.route,
              inneholdsbeskrivelse: updatedRow.inneholdsbeskrivelse,
            };
          }

          if (updatedRow.bpx !== undefined) {
            nextLeg.bpx = updatedRow.bpx;
          }
          if (updatedRow.pri03 !== undefined) {
            nextLeg.pri03 = updatedRow.pri03;
          }
          if (updatedRow.pri49 !== undefined) {
            nextLeg.pri49 = updatedRow.pri49;
          }
          if (updatedRow.upri03 !== undefined) {
            nextLeg.upri03 = updatedRow.upri03;
          }
          if (updatedRow.upri49 !== undefined) {
            nextLeg.upri49 = updatedRow.upri49;
          }
          if (updatedRow.parti !== undefined) {
            nextLeg.parti = updatedRow.parti;
          }
          if (updatedRow.brev !== undefined) {
            nextLeg.brev = updatedRow.brev;
          }
          if (updatedRow.pru !== undefined) {
            nextLeg.pru = updatedRow.pru;
          }
          if (updatedRow.pip !== undefined) {
            nextLeg.pip = updatedRow.pip;
          }
          if (updatedRow.tom !== undefined) {
            nextLeg.tom = updatedRow.tom;
          }

          return nextLeg;
        }),
      );
    },
    [],
  );

  useTerminalStatusTruckFillAndTimeUpdateSubscription({
    variables: {
      locationId: location.id || 0,
    },
    onSubscriptionData({ subscriptionData }) {
      if (
        subscriptionData.data?.truckFillAndTimeUpdate.refreshWholeGrid === false
      ) {
        setLegsData((prev) => {
          const subUpdatedLeg =
            subscriptionData.data?.truckFillAndTimeUpdate.routeLeg;
          if (!subUpdatedLeg) return prev;
          const updateType =
            subscriptionData.data?.truckFillAndTimeUpdate.updateType;

          const updatedLegs = updateLegById(
            prev,
            subUpdatedLeg.id,
            (currentLeg) => {
              let legResp = { ...currentLeg };
              if (updateType === TFT_UPDATE_TYPES.UPDATE_EXTRA_COLUMNS) {
                legResp.route = {
                  ...legResp.route,
                  adr: subUpdatedLeg.route?.adr,
                  inneholdsbeskrivelse:
                    subUpdatedLeg.route?.inneholdsbeskrivelse,
                  rutekode: subUpdatedLeg.route?.rutekode,
                  trainNumber: subUpdatedLeg.route?.trainNumber,
                  turnummer: subUpdatedLeg.route?.turnummer,
                };
              } else if (
                updateType === TFT_UPDATE_TYPES.UPDATE_VOLUME_DETAILS
              ) {
                const firstLoadingListItem =
                  subUpdatedLeg?.carDetails?.loadingListItems[0];
                legResp.bpx = firstLoadingListItem?.volume?.bpx;
                legResp.pri03 = firstLoadingListItem?.volume?.pri03;
                legResp.pri49 = firstLoadingListItem?.volume?.pri49;
                legResp.upri03 = firstLoadingListItem?.volume?.upri03;
                legResp.upri49 = firstLoadingListItem?.volume?.upri49;
                legResp.parti = firstLoadingListItem?.volume?.parti;
                legResp.brev = firstLoadingListItem?.volume?.brev;
                legResp.pru = firstLoadingListItem?.volume?.pru;
                legResp.pip = firstLoadingListItem?.volume?.pip;
              } else if (
                updateType === TFT_UPDATE_TYPES.UPDATE_CAR_TRAILER_DETAILS
              ) {
                legResp.togref =
                  subUpdatedLeg?.carDetails?.togref ?? legResp.togref;
                legResp.containerId =
                  subUpdatedLeg?.carDetails?.containerId ?? legResp.containerId;
                legResp.tom = subUpdatedLeg?.tom ?? legResp.tom;
                legResp.carDetails = subUpdatedLeg.carDetails
                  ? {
                      ...legResp.carDetails,
                      ...subUpdatedLeg.carDetails,
                    }
                  : legResp.carDetails;
              } else if (updateType === TFT_UPDATE_TYPES.TRUCK_FILL_AND_TIME) {
                legResp.gateNumber = subUpdatedLeg.gateNumber;
                legResp.carDetails = {
                  ...legResp.carDetails,
                  ...subUpdatedLeg.carDetails,
                };
                legResp.trailerDetails = {
                  ...legResp.trailerDetails,
                  ...subUpdatedLeg.trailerDetails,
                };
                legResp.carRegistrationNumber =
                  subUpdatedLeg.carRegistrationNumber;
                legResp.trailerRegistrationNumber =
                  subUpdatedLeg.trailerRegistrationNumber;
                legResp.terminalNickname = subUpdatedLeg.terminalNickname;
                legResp.actualCapacity =
                  subUpdatedLeg.carDetails?.actualCapacity;
                legResp.terminalNote = subUpdatedLeg.terminalNote;
                legResp.actualArrivalTime = subUpdatedLeg.actualArrivalTime;
                legResp.actualArrivalDate = subUpdatedLeg.actualArrivalDate;
                legResp.actualDepartureDate = subUpdatedLeg.actualDepartureDate;
                legResp.actualDepartureTime = subUpdatedLeg.actualDepartureTime;
                legResp.loadCar = subUpdatedLeg.loadCar;
                legResp.unloadCar = subUpdatedLeg.unloadCar;
                legResp.loadTrailer = subUpdatedLeg.loadTrailer;
                legResp.unloadTrailer = subUpdatedLeg.unloadTrailer;
                legResp.loadCarPPL = subUpdatedLeg.loadCarPPL;
                legResp.unloadCarPPL = subUpdatedLeg.unloadCarPPL;
                legResp.loadTrailerPPL = subUpdatedLeg.loadTrailerPPL;
                legResp.unloadTrailerPPL = subUpdatedLeg.unloadTrailerPPL;
                legResp.loadCages = subUpdatedLeg.loadCages;
                legResp.unloadCages = subUpdatedLeg.unloadCages;
                legResp.sealNumber = subUpdatedLeg.sealNumber;
                legResp.productionGroup = subUpdatedLeg.productionGroup;
                legResp.terminalResponsible = subUpdatedLeg.terminalResponsible;
                legResp.operator = subUpdatedLeg.operator ?? legResp.operator;
                legResp.priority = subUpdatedLeg.priority;
                legResp.receivedStatus = subUpdatedLeg.receivedStatus;
                legResp.driverName = subUpdatedLeg.routeDriverName;
              } else if (
                updateType === TFT_UPDATE_TYPES.PUBLISH_ROUTE_STATUS_UPDATE
              ) {
                legResp = applySubscriptionRouteLegStatusAndTiming(
                  legResp,
                  subUpdatedLeg,
                );
              } else {
                const firstLoadingListItem =
                  subUpdatedLeg?.carDetails?.loadingListItems[0];
                legResp.bpx = firstLoadingListItem?.volume?.bpx;
                legResp.pri03 = firstLoadingListItem?.volume?.pri03;
                legResp.pri49 = firstLoadingListItem?.volume?.pri49;
                legResp.upri03 = firstLoadingListItem?.volume?.upri03;
                legResp.upri49 = firstLoadingListItem?.volume?.upri49;
                legResp.parti = firstLoadingListItem?.volume?.parti;
                legResp.brev = firstLoadingListItem?.volume?.brev;
                legResp.pru = firstLoadingListItem?.volume?.pru;
                legResp.pip = firstLoadingListItem?.volume?.pip;
                legResp.togref =
                  subUpdatedLeg?.carDetails?.togref ?? legResp.togref;
                legResp.containerId =
                  subUpdatedLeg?.carDetails?.containerId ?? legResp.containerId;
                legResp.route = {
                  ...legResp.route,
                  adr: subUpdatedLeg.route?.adr ?? legResp.route.adr,
                  inneholdsbeskrivelse:
                    subUpdatedLeg.route?.inneholdsbeskrivelse ??
                    legResp.route.inneholdsbeskrivelse,
                  rutekode:
                    subUpdatedLeg.route?.rutekode ?? legResp.route.rutekode,
                  trainNumber:
                    subUpdatedLeg.route?.trainNumber ??
                    legResp.route.trainNumber,
                  turnummer:
                    subUpdatedLeg.route?.turnummer ?? legResp.route.turnummer,
                };
                legResp.tom = subUpdatedLeg?.tom ?? legResp.tom;
                legResp.actualArrivalTime = subUpdatedLeg.actualArrivalTime;
                legResp.actualArrivalDate = subUpdatedLeg.actualArrivalDate;
                legResp.actualDepartureTime = subUpdatedLeg.actualDepartureTime;
                legResp.actualDepartureDate = subUpdatedLeg.actualDepartureDate;
                legResp.actualDockTime = subUpdatedLeg.actualDockTime;
                legResp.actualDockDate = subUpdatedLeg.actualDockDate;
                legResp.actualUndockTime = subUpdatedLeg.actualUndockTime;
                legResp.actualUndockDate = subUpdatedLeg.actualUndockDate;
                legResp.terminalStatus =
                  subUpdatedLeg.terminalStatus ?? undefined;
                legResp.terminalStatusDescription =
                  subUpdatedLeg.terminalStatusDescription ?? undefined;
                legResp.terminalStatusUpdatedAt =
                  subUpdatedLeg.terminalStatusUpdatedAt ?? undefined;
                legResp.terminalStatusLog = (
                  subUpdatedLeg.terminalStatusLog ?? []
                ).filter((log): log is NonNullable<typeof log> => log != null);
                legResp.loadCar = subUpdatedLeg.loadCar;
                legResp.loadCarPPL = subUpdatedLeg.loadCarPPL;
                legResp.loadTrailer = subUpdatedLeg.loadTrailer;
                legResp.loadTrailerPPL = subUpdatedLeg.loadTrailerPPL;
                legResp.loadCages = subUpdatedLeg.loadCages;
                legResp.actualCapacity = subUpdatedLeg.actualCapacity;
                legResp.gateNumber = subUpdatedLeg.gateNumber;
                legResp.carRegistrationNumber =
                  subUpdatedLeg.carRegistrationNumber;
                legResp.trailerRegistrationNumber =
                  subUpdatedLeg.trailerRegistrationNumber;
                legResp.unloadCar = subUpdatedLeg.unloadCar;
                legResp.unloadCarPPL = subUpdatedLeg.unloadCarPPL;
                legResp.unloadTrailer = subUpdatedLeg.unloadTrailer;
                legResp.unloadTrailerPPL = subUpdatedLeg.unloadTrailerPPL;
                legResp.unloadCages = subUpdatedLeg.unloadCages;
                legResp.isNonArrivalMarked = subUpdatedLeg.isNonArrivalMarked;
                legResp.isSealBroken = subUpdatedLeg.isSealBroken;
                legResp.sealNumber = subUpdatedLeg.sealNumber;
                legResp.terminalNickname = subUpdatedLeg.terminalNickname;
                legResp.terminalNote = subUpdatedLeg.terminalNote;
                legResp.productionGroup = subUpdatedLeg.productionGroup;
                legResp.terminalResponsible = subUpdatedLeg.terminalResponsible;
                legResp.operator = subUpdatedLeg.operator ?? legResp.operator;
                legResp.priority = subUpdatedLeg.priority;
                legResp.arrivalStatus = subUpdatedLeg.arrivalStatus;
                legResp.note = subUpdatedLeg.note;
                legResp.files = subUpdatedLeg.files;
                legResp.updatedAt = subUpdatedLeg.updatedAt;
                legResp.receivedStatus = subUpdatedLeg.receivedStatus;
                legResp.receivedBy = subUpdatedLeg.receivedBy;
                legResp.timeWhenReceived = subUpdatedLeg.timeWhenReceived;
                legResp.kippReceivedBy = subUpdatedLeg.kippReceivedBy;
                legResp.kippTimeWhenReceived =
                  subUpdatedLeg.kippTimeWhenReceived;
                legResp.assigned = subUpdatedLeg.assigned;
                legResp.ansvBefraktare = subUpdatedLeg.ansvBefraktare;
                legResp.timeWhenAssigned = subUpdatedLeg.timeWhenAssigned;
                legResp.driverName = subUpdatedLeg.routeDriverName;
                legResp.carDetails = subUpdatedLeg.carDetails
                  ? {
                      ...legResp.carDetails,
                      ...subUpdatedLeg.carDetails,
                    }
                  : legResp.carDetails;
                legResp.trailerDetails = subUpdatedLeg.trailerDetails
                  ? {
                      ...legResp.trailerDetails,
                      ...subUpdatedLeg.trailerDetails,
                    }
                  : legResp.trailerDetails;
                legResp = applySubscriptionRouteLegStatusAndTiming(
                  legResp,
                  subUpdatedLeg,
                );
              }
              return legResp;
            },
          );

          return applyNorwaySubscriptionPatchToSiblings(
            updatedLegs,
            subUpdatedLeg,
          );
        });
      }
    },
    onError(error) {
      console.log('@@Subscription error:', error);
    },
  });

  useLegsListUpdateSubSubscription({
    variables: { legId: 0 },
    onSubscriptionData({ subscriptionData }) {
      const update = subscriptionData.data?.legsListUpdateSub;
      if (!update || update.refreshWholeGrid !== false) {
        return;
      }

      const subUpdatedLeg = update.routeLeg;
      if (!subUpdatedLeg) {
        return;
      }

      setLegsData((prev) => {
        const updatedLegs = updateLegById(
          prev,
          subUpdatedLeg.id,
          (currentLeg) =>
            applyNorwayFieldsFromSubscription(currentLeg, subUpdatedLeg),
        );

        return applyNorwaySubscriptionPatchToSiblings(
          updatedLegs,
          subUpdatedLeg,
        );
      });
    },
    onError(error) {
      console.log('@@LegsList subscription error:', error);
    },
  });

  const [refetchCheckDangerGoods, { loading: checkDangerGoodsLoading }] =
    useCheckLegsHaveAnyDangerGoodsLazyQuery({
      fetchPolicy: 'no-cache',
    });

  const getAllData = useCallback(
    (options?: { silent?: boolean }) => {
      const silent = options?.silent ?? false;

      if (!silent) {
        setLoading(true);
      }

      setOneDanger(0);
      setProductionGroupFetched(false);

      return httpClient
        .getIncomingRouteLegs({
          locationId: [location.id],
          startDateTime: tftStartDate,
          endDateTime: tftEndDate,
          palletSum: true,
        })
        .then((res) => {
          if (res.data && res.data.status === 'OK') {
            setLegsData(res.data.data.rows);
            setallRouteLegsIdArr(res.data.data.legIds);
            setLoadIncomingApi(false);
          } else if (res.data && res.data.status === 'FAIL') {
            setLegsData([]);
            console.error('# tft API error=', res.data.message);
          }

          if (!silent) {
            setLoading(false);
          }
        })
        .catch((e) => {
          console.error('# tft UI error=', e);
          if (!silent) {
            setLoading(false);
          }
        });
    },
    [httpClient, location.id, tftEndDate, tftStartDate],
  );

  const { data: getme } = useGetMeQuery();
  const currentUserId = getme?.me?.id;
  const getAllDataRef = useRef(getAllData);
  const templateRouteRefreshTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    getAllDataRef.current = getAllData;
  }, [getAllData]);

  useEffect(() => {
    return () => {
      if (templateRouteRefreshTimerRef.current) {
        clearTimeout(templateRouteRefreshTimerRef.current);
      }
    };
  }, []);

  useTemplateRouteUpdateSubSubscription({
    skip: !currentUserId,
    onSubscriptionData({ subscriptionData }) {
      const templateRouteUpdate = subscriptionData.data?.templateRouteUpdate;
      if (
        !templateRouteUpdate ||
        templateRouteUpdate.userId !== currentUserId ||
        !tftStartDate ||
        !tftEndDate
      ) {
        return;
      }

      // A single template change can emit several events; coalesce them into one refresh
      if (templateRouteRefreshTimerRef.current) {
        clearTimeout(templateRouteRefreshTimerRef.current);
      }
      templateRouteRefreshTimerRef.current = setTimeout(() => {
        templateRouteRefreshTimerRef.current = null;
        getAllDataRef.current();
      }, 1000);
    },
    onError(error) {
      console.log('@@TemplateRouteUpdate subscription error:', error);
    },
  });

  const redirectToCreateRoute = () => {
    const url = path.substring(0, path.lastIndexOf('/'));
    history.push({
      pathname: `${url}/create/sporadic-route`,
    });
  };

  const [isHideRegistered, setisHideRegistered] = useState<boolean>(false);
  const [isHideKippRegistered, setisHideKippRegistered] =
    useState<boolean>(false);
  const [isHideDone, setIsHideDone] = useState<boolean>(false);

  //for start-end range
  const { setMasterDate } = useUserConfiguration();
  const tftTableConfig = userConfigData?.getTrafficGridsConfig?.configuration
    ?.truckFillAndTime as TftPersistedTableConfigType | undefined;
  const customConfig = tftTableConfig?.custom as
    | TftCustomConfigType
    | undefined;
  const tftSavedTime = customConfig?.tftTime ?? '00:00_23:59';
  const getTFTTime = useCallback(
    (isStartTime: boolean) => {
      if (tftSavedTime && typeof tftSavedTime === 'string') {
        return isStartTime
          ? tftSavedTime.split('_')[0]
          : tftSavedTime.split('_')[1];
      } else {
        return isStartTime ? '00:00' : '23:59';
      }
    },
    [tftSavedTime],
  );

  const [selectedDates, setSelectedDates] = useState<TftDateRangeValue>([
    null,
    null,
  ]);
  const [dateError, setDateError] = useState('');
  const [draftPayload, setDraftPayload] = useState('');
  const [isQuickRange, setIsQuickRange] = useState<boolean>(false);
  const [prevRange, setPrevRange] = useState<TftDateRangeValue | null>(null);
  const [pendingDateConfigSave, setPendingDateConfigSave] =
    useState<PendingTftDateSave | null>(null);
  const lastAppliedPayloadRef = useRef('');
  const initializedConfigRef = useRef('');
  const persistDateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const buildTftConfigurationPayload = useCallback(
    (nextCustomConfig: TftCustomConfigType): TftPersistedTableConfigType => {
      const visibilityModel =
        tftTableConfig?.gridColumnVisibilityModel ??
        tftTableConfig?.colVisibility ??
        tftConfig.gridColumnVisibilityModel ??
        {};

      return {
        ...(tftTableConfig ?? {}),
        columnOrder: tftTableConfig?.columnOrder ?? tftConfig.columnOrder ?? [],
        filterModel: tftTableConfig?.filterModel ??
          tftConfig.filterModel ?? {
            items: [],
          },
        sortModel: tftTableConfig?.sortModel ?? tftConfig.sortModel ?? [],
        pinnedColumns: tftTableConfig?.pinnedColumns ??
          tftConfig.pinnedColumns ?? { left: [], right: [] },
        columnsWidth:
          tftTableConfig?.columnsWidth ?? tftConfig.columnsWidth ?? {},
        density: tftTableConfig?.density ?? tftConfig.density ?? 'compact',
        gridColumnVisibilityModel: visibilityModel,
        colVisibility: tftTableConfig?.colVisibility ?? visibilityModel,
        custom: {
          ...(customConfig ?? {}),
          ...nextCustomConfig,
        },
      };
    },
    [customConfig, tftConfig, tftTableConfig],
  );

  const persistDateConfiguration = useCallback(
    async (startStr: string, endStr: string, isSameDay: boolean) => {
      const [startDatePart, startTimePart] = startStr.split(' ');
      const [endDatePart, endTimePart] = endStr.split(' ');

      try {
        await updateTrafficGridsConfig({
          variables: {
            input: {
              tableName: TABLE_NAMES.TruckFillAndTime,
              configuration: buildTftConfigurationPayload({
                tftTime: `${startTimePart}_${endTimePart}`,
                master_date: `${startDatePart}#${endDatePart}`,
                isSameDay,
              }),
            },
          },
        });
        await refetchTrafficGridsConfig();
      } catch (e) {
        console.error('# tft configuration update error=', e);
      }
    },
    [
      buildTftConfigurationPayload,
      refetchTrafficGridsConfig,
      updateTrafficGridsConfig,
    ],
  );

  const getValidDateRangePayload = useCallback(
    (nextDates: TftDateRangeValue) => {
      const [start, end] = nextDates;
      if (!start || !end || !start.isValid() || !end.isValid()) {
        return {
          payload: '',
          startDate: '',
          endDate: '',
          error: '',
          isSameDay: false,
        };
      }

      const startDate = start.format(OUTPUT_FORMAT);
      const endDate = end.format(OUTPUT_FORMAT);

      if (!isDateTimeFormat(startDate) || !isDateTimeFormat(endDate)) {
        return {
          payload: '',
          startDate: '',
          endDate: '',
          error: t('validation.invalidDate', 'Invalid date'),
          isSameDay: false,
        };
      }

      if (!start.isBefore(end)) {
        return {
          payload: '',
          startDate: '',
          endDate: '',
          error: t('attributes.endDateAfterStart'),
          isSameDay: false,
        };
      }

      const maxAllowedEnd = start.add(MAX_RANGE_DAYS, 'day').endOf('day');
      if (end.isAfter(maxAllowedEnd)) {
        return {
          payload: '',
          startDate: '',
          endDate: '',
          error: t('validation.maxRangeDiff', { days: MAX_RANGE_DAYS }),
          isSameDay: false,
        };
      }

      return {
        payload: `${startDate}#${endDate}`,
        startDate,
        endDate,
        error: '',
        isSameDay: start.isSame(end, 'day'),
      };
    },
    [MAX_RANGE_DAYS, t],
  );

  useEffect(() => {
    const { payload, error } = getValidDateRangePayload(selectedDates);
    setDateError(error);
    setDraftPayload(payload);
  }, [getValidDateRangePayload, selectedDates]);

  useEffect(() => {
    if (loadingSavedTime) return;

    if (initializedConfigRef.current) {
      return;
    }

    initializedConfigRef.current = 'initialized';

    const tftTime = customConfig?.tftTime ?? '00:00_23:59';
    const [startTime = '00:00', endTime = '23:59'] = tftTime.split('_');
    const isSameDaySelected = customConfig?.isSameDay ?? false;
    const masterDate = customConfig?.master_date;

    let initialStart: Dayjs | null = null;
    let initialEnd: Dayjs | null = null;

    if (masterDate) {
      const [savedStartDate, savedEndDate] = masterDate.split('#');
      initialStart = parseDateTimeValue(`${savedStartDate} ${startTime}`);
      initialEnd = parseDateTimeValue(`${savedEndDate} ${endTime}`);
    }

    if (!initialStart || !initialEnd) {
      const currTimeHour = new Date().getHours();
      const currTimeMinute = new Date().getMinutes();
      const isSameProdDayFound =
        !isSameDaySelected &&
        timeToMinute(`${currTimeHour}:${currTimeMinute}`) <
          timeToMinute(getTFTTime(false));

      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);
      const baseDate = isSameProdDayFound
        ? dayjs().subtract(1, 'day')
        : dayjs();

      initialStart = baseDate
        .hour(startHour || 0)
        .minute(startMinute || 0)
        .second(0)
        .millisecond(0);

      const endBaseDate = isSameDaySelected ? baseDate : baseDate.add(1, 'day');
      initialEnd = endBaseDate
        .hour(endHour || 0)
        .minute(endMinute || 0)
        .second(0)
        .millisecond(0);
    }

    const initialDates: TftDateRangeValue = [initialStart, initialEnd];
    const { payload, startDate, endDate, error } =
      getValidDateRangePayload(initialDates);

    setSelectedDates(initialDates);
    setDateError(error);
    setDraftPayload(payload);

    if (payload && startDate && endDate) {
      lastAppliedPayloadRef.current = payload;
      setTftStartDate(startDate);
      setTftEndDate(endDate);
      setLoadIncomingApi(true);
    }
  }, [
    customConfig?.isSameDay,
    customConfig?.master_date,
    customConfig?.tftTime,
    getTFTTime,
    getValidDateRangePayload,
    loadingSavedTime,
  ]);

  const [
    refetchLatestProductionGroup,
    { data: productionGroupsData, loading: productionGroupLoading },
  ] = useGetProductionGroupLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (loadingSavedTime || !pendingDateConfigSave) {
      return;
    }

    persistDateConfiguration(
      pendingDateConfigSave.startStr,
      pendingDateConfigSave.endStr,
      pendingDateConfigSave.isSameDay,
    ).finally(() => {
      setPendingDateConfigSave(null);
    });
  }, [loadingSavedTime, pendingDateConfigSave, persistDateConfiguration]);

  const fetchWithRange = useCallback(
    async (startStr: string, endStr: string, isSameDayOverride?: boolean) => {
      const isSameDay =
        typeof isSameDayOverride === 'boolean'
          ? isSameDayOverride
          : startStr.split(' ')[0] === endStr.split(' ')[0];

      setTftStartDate(startStr);
      setTftEndDate(endStr);
      setMasterDate(`${startStr}#${endStr}`, true, isSameDay);

      if (loadingSavedTime) {
        setPendingDateConfigSave({ startStr, endStr, isSameDay });
      } else {
        // Debounce the persistence call so rapid prev/next clicks only fire
        // a single UpdateTrafficGridsConfig mutation for the final range.
        if (persistDateTimerRef.current) {
          clearTimeout(persistDateTimerRef.current);
        }
        persistDateTimerRef.current = setTimeout(() => {
          persistDateTimerRef.current = null;
          // Intentionally fire-and-forget: persistDateConfiguration handles
          // its own errors today, but guard against future changes (and
          // satisfy no-floating-promises) with an explicit `void` + catch
          // so a rejection here can never become an unhandled rejection.
          void persistDateConfiguration(startStr, endStr, isSameDay).catch(
            (e) => {
              console.error('# tft debounced configuration update error=', e);
            },
          );
        }, 400);
      }

      setLoadIncomingApi(true);
    },
    [loadingSavedTime, persistDateConfiguration, setMasterDate],
  );

  const handleQuickRangeToggle = useCallback(() => {
    const [start, end] = selectedDates;
    if (!start || !end || !start.isValid() || !end.isValid()) {
      return;
    }

    if (!isQuickRange) {
      setPrevRange(selectedDates);

      const today = dayjs();
      const quickStart = today
        .hour(start.hour())
        .minute(start.minute())
        .second(0)
        .millisecond(0)
        .subtract(MAX_RANGE_DAYS, 'day');
      const quickEnd = today
        .hour(end.hour())
        .minute(end.minute())
        .second(0)
        .millisecond(0);

      const nextRange: TftDateRangeValue = [quickStart, quickEnd];
      const { payload, startDate, endDate, error, isSameDay } =
        getValidDateRangePayload(nextRange);

      setSelectedDates(nextRange);
      setDateError(error);
      setDraftPayload(payload);

      if (payload && startDate && endDate) {
        lastAppliedPayloadRef.current = payload;
        setIsQuickRange(true);
        fetchWithRange(startDate, endDate, isSameDay);
      }

      return;
    }

    if (prevRange?.[0] && prevRange?.[1]) {
      const { payload, startDate, endDate, error, isSameDay } =
        getValidDateRangePayload(prevRange);

      setSelectedDates(prevRange);
      setDateError(error);
      setDraftPayload(payload);

      if (payload && startDate && endDate) {
        lastAppliedPayloadRef.current = payload;
        fetchWithRange(startDate, endDate, isSameDay);
      }
    }

    setIsQuickRange(false);
    setPrevRange(null);
  }, [
    MAX_RANGE_DAYS,
    fetchWithRange,
    getValidDateRangePayload,
    isQuickRange,
    prevRange,
    selectedDates,
  ]);

  const handleDateRangeChange = (newValue: TftDateRangeValue) => {
    setSelectedDates(newValue);
  };

  const applySelectedDateRange = useCallback(
    async (nextDates: TftDateRangeValue, force = false) => {
      const { payload, startDate, endDate, error, isSameDay } =
        getValidDateRangePayload(nextDates);

      setDateError(error);

      if (!payload || !startDate || !endDate) {
        return;
      }

      if (!force && payload === lastAppliedPayloadRef.current) {
        return;
      }

      lastAppliedPayloadRef.current = payload;
      await fetchWithRange(startDate, endDate, isSameDay);
    },
    [fetchWithRange, getValidDateRangePayload],
  );

  const shiftDateRange = async (isNext: boolean) => {
    const [start, end] = selectedDates;
    if (!start || !end || !start.isValid() || !end.isValid()) {
      return;
    }

    const shiftBy = isNext ? 1 : -1;
    const shiftedDates: TftDateRangeValue = [
      start.add(shiftBy, 'day'),
      end.add(shiftBy, 'day'),
    ];

    setSelectedDates(shiftedDates);
    await applySelectedDateRange(shiftedDates, true);
  };

  const canShiftDateRange =
    !!selectedDates[0] &&
    !!selectedDates[1] &&
    selectedDates[0].isValid() &&
    selectedDates[1].isValid() &&
    !dateError;

  const handleApply = async () => {
    await applySelectedDateRange(selectedDates);
  };

  useEffect(() => {
    if (productionGroupsData) {
      setProductionGroupArray(productionGroupsData.getProductionGroup);
    }
  }, [productionGroupsData, legsData]);

  useEffect(
    () => () => {
      // Clear any pending debounced persistDateConfiguration call so the
      // Apollo mutation does not fire after the component unmounts.
      if (persistDateTimerRef.current) {
        clearTimeout(persistDateTimerRef.current);
        persistDateTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (tftStartDate !== '' && tftEndDate !== '' && loadIncomingApi) {
      // Debounce so rapid prev/next-day shifts don't fire one API call per
      // click — only the final range after the user stops clicking is fetched.
      const timeoutId = setTimeout(() => {
        setLoadIncomingApi(false);
        getAllData();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [getAllData, loadIncomingApi, location.id, tftEndDate, tftStartDate]);

  useEffect(() => {
    const element = document.getElementById('truckFillGrid');
    const positions = element?.getBoundingClientRect();

    if (positions) {
      setGridTop(positions.top);
    }
  }, []);

  return (
    <ToggleFullScreen
      fullScreenBtnClass={classes.fullScreenBtnLeft}
      persistKey="tft.fullscreen"
    >
      {(fullScreenButton, isExpanded) => (
        <>
          <Grid container className={classes.rootGrid}>
            <Snackbar
              open={errorInfo?.errorInField}
              key={'alert'}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              autoHideDuration={5000}
              onClose={() => {
                setErrorInfo({ errorInField: false, errorMessage: '' });
              }}
            >
              <Alert
                onClose={() => {
                  setErrorInfo({ errorInField: false, errorMessage: '' });
                }}
                severity="error"
                variant="filled"
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {errorInfo?.errorMessage}
              </Alert>
            </Snackbar>
            <Grid item xs={12}>
              <Paper className={classes.selectDateRoot} elevation={0}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 4,
                      height: 24,
                      borderRadius: 2,
                      backgroundColor: 'primary.main',
                      mr: 0.5,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.05rem',
                      letterSpacing: '0.01em',
                      color: 'text.primary',
                    }}
                  >
                    {location.name}
                  </Typography>
                </Box>

                <Box className={classes.filterButtonRoot}>
                  <Box
                    sx={{
                      width: 200,
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                      },
                    }}
                  >
                    <GridFiltersDropdown
                      tableName={TABLE_NAMES.TruckFillAndTime}
                      config={tftConfig}
                      setConfig={setTftConfig}
                    />
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="traffic-type-filter-label" shrink>
                      {t('attributes.trafficType')}
                    </InputLabel>
                    <Select
                      labelId="traffic-type-filter-label"
                      id="traffic-type-filter"
                      className={classes.trafficTypeSelect}
                      value={selectedTrafficType}
                      label={t('attributes.trafficType')}
                      displayEmpty
                      renderValue={(value) => (value ? value : 'All')}
                      onChange={(event: SelectChangeEvent<string>) => {
                        setSelectedTrafficType(event.target.value as string);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {TRAFFIC_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant={isQuickRange ? 'contained' : 'outlined'}
                    className={classes.actionButton}
                    size="small"
                    onClick={handleQuickRangeToggle}
                  >
                    {t('attributes.lastDays', { days: MAX_RANGE_DAYS })}
                  </Button>

                  <Tooltip title="Previous Date" arrow>
                    <span>
                      <IconButton
                        disabled={!canShiftDateRange}
                        className={classes.navButton}
                        sx={{
                          backgroundColor: canShiftDateRange
                            ? 'rgba(105,188,70,0.08)'
                            : undefined,
                        }}
                        onClick={() => shiftDateRange(false)}
                      >
                        <PreviousIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimeRangePicker
                      value={selectedDates}
                      onChange={(newValue) => {
                        handleDateRangeChange(newValue);
                      }}
                      onAccept={(newValue) => {
                        applySelectedDateRange(newValue, true);
                      }}
                      slots={{
                        shortcuts: AlwaysEnabledShortcuts,
                      }}
                      ampm={false}
                      format={dayjsDisplayFormat}
                      timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                      maxDateTime={
                        selectedDates[0]
                          ? selectedDates[0]
                              .add(MAX_RANGE_DAYS, 'day')
                              .endOf('day')
                          : undefined
                      }
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          error: !!dateError,
                          helperText: dateError,
                          InputProps: {
                            style: { fontSize: 15 },
                            classes: { input: classes.inputValTime },
                            maxRows: 1,
                          },
                        },
                        shortcuts: {
                          items: [
                            {
                              label: 'Today',
                              getValue: (): TftDateRangeValue => {
                                const sh = selectedDates[0]?.isValid()
                                  ? selectedDates[0].hour()
                                  : 0;
                                const sm = selectedDates[0]?.isValid()
                                  ? selectedDates[0].minute()
                                  : 0;
                                const eh = selectedDates[1]?.isValid()
                                  ? selectedDates[1].hour()
                                  : 23;
                                const em = selectedDates[1]?.isValid()
                                  ? selectedDates[1].minute()
                                  : 59;
                                const today = dayjs();
                                const start = today
                                  .hour(sh)
                                  .minute(sm)
                                  .second(0)
                                  .millisecond(0);
                                let end = today
                                  .hour(eh)
                                  .minute(em)
                                  .second(59)
                                  .millisecond(999);
                                if (!end.isAfter(start)) {
                                  end = today.endOf('day');
                                }
                                return [start, end];
                              },
                            },
                            {
                              label: 'Today + Tomorrow',
                              getValue: (): TftDateRangeValue => {
                                const sh = selectedDates[0]?.isValid()
                                  ? selectedDates[0].hour()
                                  : 0;
                                const sm = selectedDates[0]?.isValid()
                                  ? selectedDates[0].minute()
                                  : 0;
                                const eh = selectedDates[1]?.isValid()
                                  ? selectedDates[1].hour()
                                  : 23;
                                const em = selectedDates[1]?.isValid()
                                  ? selectedDates[1].minute()
                                  : 59;
                                const today = dayjs();
                                return [
                                  today
                                    .hour(sh)
                                    .minute(sm)
                                    .second(0)
                                    .millisecond(0),
                                  today
                                    .add(1, 'day')
                                    .hour(eh)
                                    .minute(em)
                                    .second(59)
                                    .millisecond(999),
                                ];
                              },
                            },
                            {
                              label: 'Yesterday + Today',
                              getValue: (): TftDateRangeValue => {
                                const sh = selectedDates[0]?.isValid()
                                  ? selectedDates[0].hour()
                                  : 0;
                                const sm = selectedDates[0]?.isValid()
                                  ? selectedDates[0].minute()
                                  : 0;
                                const eh = selectedDates[1]?.isValid()
                                  ? selectedDates[1].hour()
                                  : 23;
                                const em = selectedDates[1]?.isValid()
                                  ? selectedDates[1].minute()
                                  : 59;
                                const today = dayjs();
                                return [
                                  today
                                    .subtract(1, 'day')
                                    .hour(sh)
                                    .minute(sm)
                                    .second(0)
                                    .millisecond(0),
                                  today
                                    .hour(eh)
                                    .minute(em)
                                    .second(59)
                                    .millisecond(999),
                                ];
                              },
                            },
                          ],
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <Tooltip title="Next Date" arrow>
                    <span>
                      <IconButton
                        disabled={!canShiftDateRange}
                        className={classes.navButton}
                        sx={{
                          backgroundColor: canShiftDateRange
                            ? 'rgba(105,188,70,0.08)'
                            : undefined,
                        }}
                        onClick={() => shiftDateRange(true)}
                      >
                        <NextIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    className={classes.actionButton}
                    variant="contained"
                    size="small"
                    disabled={
                      !draftPayload ||
                      !!dateError ||
                      draftPayload === lastAppliedPayloadRef.current
                    }
                    onClick={() => {
                      handleApply();
                    }}
                  >
                    {`${t('attributes.view')}`}
                  </Button>
                  {fullScreenButton}
                </Box>
              </Paper>
            </Grid>
            <Grid
              container
              className={`tft-secondary-actions ${classes.secondaryToolbar}`}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid
                item
                container
                style={{ justifyContent: 'flex-start', width: 'auto', gap: 8 }}
                alignItems="center"
              >
                <Tooltip title={t('attributes.view')} arrow>
                  <IconButton
                    size="small"
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      width: 32,
                      height: 32,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        borderColor: 'primary.main',
                        color: 'primary.dark',
                        transform: 'rotate(90deg)',
                      },
                    }}
                    onClick={() => {
                      if (tftStartDate !== '' && tftEndDate !== '')
                        getAllData();
                    }}
                  >
                    <RefreshOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Button
                  className={classes.actionButton}
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => redirectToCreateRoute()}
                >
                  {`${t('attributes.addTruck')}`}
                </Button>

                <Button
                  className={classes.actionButton}
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setUnplannedRouteModalOpen(true)}
                >
                  {t('attributes.addUnplannedRoute')}
                </Button>

                <Divider orientation="vertical" flexItem />

                {isProdGroupFetched == false && legsData.length > 0 ? (
                  <Button
                    variant="contained"
                    className={classes.actionButton}
                    size="small"
                    disabled={productionGroupLoading || loading}
                    onClick={() => {
                      refetchLatestProductionGroup({
                        variables: {
                          terminalId: location.id as number,
                          startDate: tftStartDate,
                          endDate: tftEndDate,
                        },
                      })
                        .then(() => {
                          setProductionGroupFetched(true);
                        })
                        .catch((e) => {
                          console.error(e);
                          setProductionGroupFetched(false);
                        });
                    }}
                  >
                    {`${t('attributes.fetchProductionGroup')}`}
                  </Button>
                ) : null}
                {productionGroupArray.length > 0 && isProdGroupFetched ? (
                  <TextField
                    id="production-group"
                    style={{ width: 150, marginLeft: 5, padding: 0 }}
                    select
                    size="small"
                    variant="standard"
                    value={productionGroup}
                    label={t(`attributes.productionGroup`)}
                    onChange={(event) => {
                      setProductionGroup(event.target.value as string);
                    }}
                  >
                    <MenuItem value="All-Groups">
                      {t(`attributes.allGroups`)}
                    </MenuItem>
                    {productionGroupArray.map((productionGroup) => (
                      <MenuItem value={productionGroup}>
                        {productionGroup}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                {/* <Box style={{ marginLeft: 2, width: '200px' }}>
                <GridFiltersDropdown
                  tableName={TABLE_NAMES.TruckFillAndTime}
                  config={tftConfig}
                  setConfig={setTftConfig}
                />
              </Box> */}
              </Grid>
              <Grid
                item
                container
                direction="row"
                style={{ justifyContent: 'flex-end', width: 'auto', gap: 8 }}
                alignItems="center"
              >
                <>
                  {!checkDangerGoodsLoading && isOneDanger == 1 ? (
                    <Box alignContent="center" style={{ marginRight: 2 }}>
                      <Tooltip title={`${t('attributes.dangerGoodFound')}`}>
                        <ReportIcon htmlColor="red" />
                      </Tooltip>
                    </Box>
                  ) : !checkDangerGoodsLoading && isOneDanger == 2 ? (
                    <Box alignContent="center" style={{ marginRight: 2 }}>
                      <Tooltip title={`${t('attributes.dangerGoodNotFound')}`}>
                        <ReportOffIcon htmlColor="green" />
                      </Tooltip>
                    </Box>
                  ) : null}
                  {isOneDanger == 0 && allRouteLegsIdArr.length > 0 ? (
                    <Button
                      variant="contained"
                      className={classes.actionButton}
                      size="small"
                      disabled={checkDangerGoodsLoading || loading}
                      onClick={() => {
                        refetchCheckDangerGoods({
                          variables: { legIds: allRouteLegsIdArr },
                        })
                          .then((res) => {
                            setOneDanger(
                              res.data?.checkLegsHaveAnyDangerGoods === true
                                ? 1
                                : 2,
                            );
                          })
                          .catch((e) => {
                            console.error(e);
                            setOneDanger(0);
                          });
                      }}
                    >
                      {`${t('attributes.checkDangerGood')}`}
                    </Button>
                  ) : null}

                  {legsData.length === 0 ? null : (
                    <>
                      <Button
                        variant={isHideRegistered ? 'contained' : 'outlined'}
                        className={classes.actionButton}
                        size="small"
                        onClick={() => setisHideRegistered(!isHideRegistered)}
                      >
                        {!isHideRegistered
                          ? t('attributes.hideRegistered')
                          : t('attributes.showRegistered')}
                      </Button>
                      <Button
                        variant={
                          isHideKippRegistered ? 'contained' : 'outlined'
                        }
                        className={classes.actionButton}
                        size="small"
                        onClick={() =>
                          setisHideKippRegistered(!isHideKippRegistered)
                        }
                      >
                        {!isHideKippRegistered
                          ? t('attributes.hideKippRegistered')
                          : t('attributes.showKippRegistered')}
                      </Button>
                      <Button
                        variant={isHideDone ? 'contained' : 'outlined'}
                        className={classes.actionButton}
                        size="small"
                        onClick={() => setIsHideDone(!isHideDone)}
                      >
                        {!isHideDone ? 'Hide Done' : 'Show Done'}
                      </Button>
                    </>
                  )}
                </>
              </Grid>
            </Grid>

            {/* Data grid */}
            <Grid item xs={12} id="truckFillGrid" sx={{ mt: 1 }}>
              {loading ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  mt="20vh"
                  gap={2}
                  sx={{
                    animation: 'fadeIn 0.3s ease-in',
                    '@keyframes fadeIn': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                  }}
                >
                  <CircularProgress
                    size={44}
                    thickness={3.5}
                    sx={{ color: 'primary.main' }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {t('validation.loadingApi')}
                  </Typography>
                </Box>
              ) : (
                <ListIncomingRoutes
                  loading={loading}
                  legs={legsData}
                  location={location}
                  selectedLocation={location}
                  gridBoxHeight={
                    isExpanded
                      ? window.innerHeight - (gridTop + gridTop / 2)
                      : window.innerHeight - (gridTop + gridTop / 6)
                  }
                  isHideRegistered={isHideRegistered}
                  isHideKippRegistered={isHideKippRegistered}
                  isHideDone={isHideDone}
                  group={productionGroup}
                  onRefreshData={() => getAllData({ silent: true })}
                  onTerminalStatusUpdated={handleTerminalStatusUpdated}
                  onNorwayColumnsUpdated={handleNorwayColumnsUpdated}
                  tftConfig={tftConfig}
                  setTftConfig={setTftConfig}
                  selectedTrafficType={selectedTrafficType}
                />
              )}
            </Grid>
          </Grid>

          {/* Add Unplanned Route Modal */}
          <AddUnplannedRouteModal
            open={unplannedRouteModalOpen}
            onClose={() => setUnplannedRouteModalOpen(false)}
            terminalLocationId={location?.id ?? 0}
            terminalLocationName={location?.name ?? ''}
            transportationDate={
              selectedDates[0]?.isValid()
                ? selectedDates[0].format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD')
            }
            onCreated={() => getAllData()}
          />
        </>
      )}
    </ToggleFullScreen>
  );
}
